import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { TenantContextService } from '../tenant/tenant-context.service';

/**
 * Exige que un SUPER_ADMIN haya elegido tenant ("Operar como") ANTES de entrar a
 * una ruta cuyos endpoints son tenant-scoped.
 *
 * Sin esto, la pantalla cargaba, disparaba sus peticiones y el backend contestaba
 * 403 `ACT_AS_TENANT_REQUIRED` a cada una: el usuario veía un toast rojo por
 * request (y uno más por cada pantalla que visitara), sin que ninguno le dijera
 * cómo salir del paso. El dato ya estaba del lado del cliente — sé que sos super
 * admin y sé que no elegiste tenant —, así que la pregunta se hace antes de
 * navegar en vez de descubrirse por una respuesta de error.
 *
 * Sólo aplica a SUPER_ADMIN: los demás roles llevan su `tenant_id` en el JWT y
 * nunca necesitan act-as.
 *
 * Lo que este guard NO puede evitar: que el tenant elegido esté suspendido o
 * terminado (`ACT_AS_TENANT_NOT_OPERABLE`, 403). Eso sólo lo sabe el servidor
 * —salvo que el selector no llegue a ofrecer esos tenants—, así que ese error
 * sigue llegando por respuesta y el toast del ErrorInterceptor sigue siendo su
 * canal.
 *
 * La autorización REAL la hace el backend; esto es sólo routing, igual que
 * `permissionGuard`.
 */
export const actAsTenantGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const tenantCtx = inject(TenantContextService);
  const router = inject(Router);

  if (!auth.isSuperAdmin()) return true;
  if (tenantCtx.actingTenant()) return true;

  // `returnUrl` para volver exactamente adonde iba, no a un home genérico: el
  // super admin que pegó una URL profunda (o recargó con F5) la recupera tras
  // elegir tenant.
  return router.createUrlTree(['/app/select-tenant'], {
    queryParams: { returnUrl: state.url },
  });
};
